import { motion, useReducedMotion } from "framer-motion";
import { BookButton, WhatsAppButton, DisclaimerNote } from "../Cta";
import { PhotoSlot } from "../PhotoSlot";

const MaskedLine = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <span className={`block ${className}`}>
      <motion.span
        className="block will-change-transform"
        initial={reduce ? false : { opacity: 0, y: "45%" }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const Hero = () => {
  return (
    <section className="relative" data-testid="hero-section">
      <div className="mx-auto max-w-6xl px-5 md:px-8 pt-32 md:pt-44 pb-16 md:pb-28">
        <div className="grid gap-14 md:grid-cols-12 md:gap-8 items-end">
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="text-xs md:text-sm tracking-[0.25em] uppercase text-pine font-semibold"
              data-testid="hero-overline"
            >
              Walk It Through
            </motion.p>
            <h1
              className="mt-6 overflow-visible font-serif text-[2.6rem] leading-[1.15] sm:text-6xl lg:text-7xl tracking-tight text-ink"
              data-testid="hero-heading"
            >
              <MaskedLine delay={0.2}>You don’t have to</MaskedLine>
              <MaskedLine delay={0.32}>solve your whole</MaskedLine>
              <MaskedLine delay={0.44} className="italic text-pine leading-[1.3] pb-[0.08em]">
                future today.
              </MaskedLine>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <p
                className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/70"
                data-testid="hero-subheading"
              >
                Calm, independent support for fathers navigating separation,
                co-parenting and changing family relationships.
              </p>
              <p className="mt-4 max-w-xl text-base md:text-lg leading-relaxed text-ink/70">
                When everything feels overwhelming, sometimes the most useful
                thing you can do is slow things down and talk through what
                actually needs to happen next.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <BookButton testid="hero-book-cta" />
                <WhatsAppButton testid="hero-whatsapp-cta" />
              </div>
              <p
                className="mt-6 text-sm text-ink/50 tracking-wide"
                data-testid="hero-microcopy"
              >
                30 minutes · Phone call · Walking encouraged, not required
              </p>
              <DisclaimerNote className="mt-4" testid="hero-disclaimer" />
            </motion.div>
          </div>
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <PhotoSlot
                src="/images/hero-coast.jpg"
                alt="Luke walking along the Portuguese coast path, pushing the pram"
                ratio="aspect-[4/5]"
                position="object-[34%_center]"
                testid="hero-photo"
              />
            </motion.div>
            <p className="mt-3 text-sm tracking-wide text-ink/50" data-testid="hero-caption">
              The walk is optional. The conversation is the point.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
